import React, { ReactNode } from 'react';
import { DepotBrandingHeader } from './DepotBrandingHeader';
import type { DepotBrandingDto } from '../types';

interface Props {
  children: ReactNode;
  branding?: DepotBrandingDto;
}

/**
 * Coquille visuelle commune à tous les écrans du dépôt sécurisé.
 *
 * Volontairement standalone (pas de DashboardLayout) : le souscripteur
 * arrive depuis un email, il n'est pas dans l'app Espace Pro et n'a rien
 * à voir avec le menu / sidebar / org switcher. On veut un écran plein
 * page sobre, centré, qui marche aussi bien desktop que mobile.
 *
 * Quand un `branding` est fourni (renvoyé par /etat) :
 * - un strip de couleur primaire est ajouté en haut de la carte (accent
 *   visuel discret, identifiant le courtier au premier coup d'œil),
 * - un `DepotBrandingHeader` est inséré au-dessus du contenu avec logo
 *   + nom courtier + produit.
 */
export const DepotLayout: React.FC<Props> = ({ children, branding }) => {
  // primaryColor vient du back en hex `#RRGGBB`. On lui fait confiance ;
  // si jamais c'est mal formé le navigateur l'ignorera et le strip sera gris
  // (vu qu'on ne mettrait rien à la place).
  const primaryColor = branding?.primaryColor;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-primary/10 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {primaryColor && (
            <div
              aria-hidden="true"
              className="h-2"
              style={{ backgroundColor: primaryColor }}
            />
          )}
          <div className="p-8 sm:p-10">
            {branding && <DepotBrandingHeader branding={branding} />}
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};
